
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Check, X } from "lucide-react";

interface PasswordRule {
  id: string;
  text: string;
  test: (password: string) => boolean;
}

const passwordRules: PasswordRule[] = [
  {
    id: "length",
    text: "Mínimo de 8 caracteres",
    test: (password) => password.length >= 8,
  },
  {
    id: "uppercase",
    text: "Pelo menos uma letra maiúscula",
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: "lowercase",
    text: "Pelo menos uma letra minúscula",
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: "number",
    text: "Pelo menos um número",
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: "special",
    text: "Pelo menos um caractere especial (!@#$%^&*)",
    test: (password) => /[!@#$%^&*]/.test(password),
  },
];

export const PasswordChangeDialog = ({ 
  isOpen, 
  onClose 
}: { 
  isOpen: boolean; 
  onClose: () => void;
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const validatePassword = (password: string) => {
    return passwordRules.every((rule) => rule.test(password));
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "As senhas não coincidem",
      });
      return;
    }

    if (!validatePassword(newPassword)) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "A senha não atende aos requisitos mínimos",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Senha alterada com sucesso",
        description: "Sua nova senha foi configurada",
      });
      
      // Reload the page to update the session
      window.location.reload();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao alterar senha",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Don't allow closing the dialog by clicking outside
      if (!open) return;
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar Senha</DialogTitle>
          <DialogDescription>
            Por favor, altere sua senha para continuar. Sua nova senha deve atender aos seguintes requisitos:
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-4">
            {passwordRules.map((rule) => (
              <div 
                key={rule.id} 
                className="flex items-center gap-2 text-sm"
              >
                {rule.test(newPassword) ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
                <span>{rule.text}</span>
              </div>
            ))}
          </div>
          <div className="grid gap-2">
            <Input
              type="password"
              placeholder="Nova senha"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Confirme a nova senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <Button 
            onClick={handlePasswordChange} 
            disabled={isLoading || !validatePassword(newPassword) || newPassword !== confirmPassword}
            className="bg-[#0073B7] hover:bg-[#004080]"
          >
            {isLoading ? "Alterando..." : "Alterar Senha"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
